import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, X, Trash, Eye, Image as ImageIcon, TextAa, MusicNote, ArrowLeft } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusManagement = ({ user }) => {
  const [myStatuses, setMyStatuses] = useState([]);
  const [otherStatuses, setOtherStatuses] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusType, setStatusType] = useState('text');
  const [statusContent, setStatusContent] = useState('');
  const [imageCaption, setImageCaption] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedStatus, setExpandedStatus] = useState(null);
  const [viewingStatus, setViewingStatus] = useState(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);

  useEffect(() => {
    fetchMyStatuses();
    fetchOtherStatuses();
  }, []);

  const fetchMyStatuses = async () => {
    try {
      const response = await axios.get(`${API}/status/my-statuses`, {
        params: { user_id: user.id }
      });
      setMyStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherStatuses = async () => {
    try {
      const response = await axios.get(`${API}/status/all`);
      const others = (response.data || []).filter(s => s.user_id !== user.id);
      // Group by user
      const grouped = {};
      others.forEach(s => {
        if (!grouped[s.user_id]) grouped[s.user_id] = { user_name: s.user_name, user_role: s.user_role, statuses: [] };
        grouped[s.user_id].statuses.push(s);
      });
      setOtherStatuses(Object.values(grouped));
    } catch (e) {
      console.error('Failed to fetch other statuses:', e);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { toast.error('Invalid file type'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5MB.'); return; }
    setSelectedImage(file);
  };

  const handleCreateStatus = async () => {
    try {
      setUploading(true);
      let imageUrl = null;
      let audioUrl = null;

      if (statusType === 'image' && selectedImage) {
        const formData = new FormData();
        formData.append('file', selectedImage);
        const uploadResponse = await axios.post(`${API}/status/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = uploadResponse.data.url || `/uploads/status_images/${uploadResponse.data.filename}`;
      }

      if (statusType === 'audio' && selectedAudio) {
        const formData = new FormData();
        formData.append('file', selectedAudio);
        formData.append('media_type', 'audio');
        const uploadResponse = await axios.post(`${API}/upload/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        audioUrl = uploadResponse.data.url;
      }

      await axios.post(`${API}/status/create`, {
        user_id: user.id,
        user_role: user.role,
        type: statusType,
        content: statusType === 'text' ? statusContent : null,
        image_url: statusType === 'image' ? imageUrl : null,
        audio_url: audioUrl,
        caption: imageCaption || null
      });

      toast.success('Status posted!');
      setShowAddModal(false);
      setStatusType('text');
      setStatusContent('');
      setSelectedImage(null);
      setSelectedAudio(null);
      setImageCaption('');
      fetchMyStatuses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create status');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteStatus = async (statusId) => {
    if (!window.confirm('Delete this status?')) return;
    try {
      await axios.delete(`${API}/status/${statusId}`, { params: { user_id: user.id } });
      toast.success('Deleted!');
      fetchMyStatuses();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffHrs > 0) return `${diffHrs}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API}${url}`;
  };

  const getRoleBadge = (role) => {
    if (role === 'super_admin') return { text: 'Admin', color: 'bg-red-500' };
    if (role?.includes('deposit')) return { text: 'Deposit', color: 'bg-blue-500' };
    if (role?.includes('withdrawal')) return { text: 'Withdrawal', color: 'bg-orange-500' };
    if (role?.includes('support')) return { text: 'Support', color: 'bg-wa-primary-strong' };
    if (role?.includes('inquiry')) return { text: 'Inquiry', color: 'bg-purple-500' };
    return { text: 'Handler', color: 'bg-gray-500' };
  };

  if (loading) {
    return <div className="p-6 text-center text-wa-text-secondary">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {/* My Status */}
      <div className="bg-wa-panel rounded-xl shadow-sm overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-wa-text-primary">My Status</h2>
            <p className="text-xs text-wa-text-secondary">Expires in 24 hours</p>
          </div>
          <button onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-[#008069] text-white rounded-lg hover:bg-[#006e5a] text-sm font-medium"
            data-testid="add-status-btn">
            <Plus size={16} weight="bold" /> Add Status
          </button>
        </div>

        {myStatuses.length === 0 ? (
          <p className="p-4 text-center text-wa-text-secondary text-sm">No statuses yet</p>
        ) : (
          <div className="divide-y">
            {myStatuses.map(status => (
              <div key={status.id} className="p-3 hover:bg-wa-hover">
                <div className="flex gap-3">
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-200 cursor-pointer"
                    onClick={() => setViewingStatus(status)}>
                    {status.type === 'image' && status.image_url ? (
                      <img src={getImageUrl(status.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : status.type === 'audio' ? (
                      <div className="w-full h-full bg-wa-hover flex items-center justify-center">
                        <MusicNote size={24} className="text-purple-600" />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-wa-hover flex items-center justify-center">
                        <TextAa size={24} className="text-green-600" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0" onClick={() => setViewingStatus(status)}>
                    <p className="text-sm font-medium text-wa-text-primary truncate cursor-pointer">
                      {status.type === 'text' ? status.content : (status.caption || (status.type === 'audio' ? 'Audio Status' : 'Photo Status'))}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-wa-text-secondary">
                      <span>{getTimeAgo(status.created_at)}</span>
                      <span className={status.is_expired ? 'text-red-500' : 'text-green-600'}>
                        {status.is_expired ? 'Expired' : 'Active'}
                      </span>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setExpandedStatus(expandedStatus === status.id ? null : status.id); }}
                      className="flex items-center gap-1 mt-1 text-xs text-blue-600">
                      <Eye size={12} /> {status.view_count || 0} views
                    </button>
                    {expandedStatus === status.id && status.viewers?.length > 0 && (
                      <div className="mt-2 bg-wa-hover rounded p-2 text-xs space-y-1">
                        {status.viewers.map(v => (
                          <div key={v.id} className="flex justify-between">
                            <span>{v.name}</span><span className="text-wa-text-secondary">{getTimeAgo(v.viewed_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Delete */}
                  <button onClick={() => handleDeleteStatus(status.id)} className="p-1.5 text-red-400 hover:text-red-600 self-start">
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Other Statuses - Show each status separately */}
      {otherStatuses.length > 0 && (
        <div className="bg-wa-panel rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="text-lg font-bold text-wa-text-primary">Recent Updates</h2>
          </div>
          <div className="divide-y">
            {otherStatuses.map((group) => 
              group.statuses.map((status, sIdx) => {
                const badge = getRoleBadge(group.user_role);
                return (
                  <div key={status.id || sIdx} className="p-3 hover:bg-wa-hover cursor-pointer"
                    onClick={() => setViewingStatus(status)}>
                    <div className="flex gap-3 items-center">
                      <div className="w-12 h-12 rounded-full ring-2 ring-[#008069] ring-offset-2 overflow-hidden bg-gradient-to-br from-[#00a884] to-[#008069] flex items-center justify-center flex-shrink-0">
                        {status.type === 'image' && status.image_url ? (
                          <img src={getImageUrl(status.image_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-white font-bold text-lg">{(group.user_name || 'U')[0].toUpperCase()}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-wa-text-primary">{group.user_name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full text-white ${badge.color}`}>{badge.text}</span>
                        </div>
                        <p className="text-xs text-wa-text-secondary mt-0.5 truncate">
                          {status.type === 'text' ? status.content : (status.caption || (status.type === 'audio' ? 'Audio' : 'Photo'))} · {getTimeAgo(status.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Fullscreen Status Viewer (WhatsApp style) */}
      {viewingStatus && (
        <div className="fixed inset-0 bg-black z-[100] flex flex-col" data-testid="status-viewer">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 bg-black/50">
            <button onClick={() => setViewingStatus(null)} className="text-white p-1">
              <ArrowLeft size={24} weight="bold" />
            </button>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] flex items-center justify-center">
              <span className="text-white font-bold">{(viewingStatus.user_name || 'U')[0].toUpperCase()}</span>
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-sm">{viewingStatus.user_name || 'Unknown'}</p>
              <p className="text-white/60 text-xs">{getTimeAgo(viewingStatus.created_at)}</p>
            </div>
            {viewingStatus.user_id === user.id && (
              <div className="flex items-center gap-1 text-white/70 text-xs">
                <Eye size={14} /> {viewingStatus.view_count || 0} views
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center p-4">
            {viewingStatus.type === 'image' && viewingStatus.image_url ? (
              <img src={getImageUrl(viewingStatus.image_url)} alt="Status"
                className="max-w-full max-h-full object-contain rounded-lg" />
            ) : viewingStatus.type === 'audio' && viewingStatus.audio_url ? (
              <div className="bg-white/10 rounded-2xl p-8 flex flex-col items-center gap-4">
                <MusicNote size={64} className="text-white/80" />
                <audio src={getImageUrl(viewingStatus.audio_url)} controls className="w-72" autoPlay />
              </div>
            ) : (
              <div className="bg-[#008069] rounded-2xl p-8 max-w-md">
                <p className="text-white text-xl text-center font-medium">{viewingStatus.content}</p>
              </div>
            )}
          </div>

          {/* Caption */}
          {viewingStatus.caption && (
            <div className="p-4 bg-black/60">
              <p className="text-white text-center text-sm">{viewingStatus.caption}</p>
            </div>
          )}
        </div>
      )}

      {/* Add Status Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Add New Status</h3>
              <button onClick={() => { setShowAddModal(false); setStatusType('text'); setStatusContent(''); setSelectedImage(null); setSelectedAudio(null); setImageCaption(''); }}
                className="text-wa-text-secondary hover:text-wa-text-primary"><X size={24} weight="bold" /></button>
            </div>

            {/* Type Selector */}
            <div className="flex gap-3 mb-4">
              {[
                { type: 'text', icon: <TextAa size={24} weight="bold" />, label: 'Text' },
                { type: 'image', icon: <ImageIcon size={24} weight="bold" />, label: 'Image' },
                { type: 'audio', icon: <MusicNote size={24} weight="bold" />, label: 'Audio' }
              ].map(t => (
                <button key={t.type}
                  onClick={() => { setStatusType(t.type); setSelectedImage(null); setSelectedAudio(null); setStatusContent(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-colors ${
                    statusType === t.type ? 'border-green-600 bg-wa-hover text-wa-text-primary' : 'border-wa-border hover:border-gray-400'
                  }`}>
                  {t.icon}<span className="font-semibold">{t.label}</span>
                </button>
              ))}
            </div>

            {/* Content Input */}
            {statusType === 'text' ? (
              <div className="mb-4">
                <textarea value={statusContent} onChange={(e) => setStatusContent(e.target.value)}
                  placeholder="What's on your mind?" rows={4}
                  className="w-full px-4 py-3 border border-wa-border rounded-lg focus:ring-2 focus:ring-green-500 resize-none" maxLength={500} />
                <p className="text-xs text-wa-text-secondary mt-1">{statusContent.length}/500</p>
              </div>
            ) : statusType === 'audio' ? (
              <div className="mb-4">
                <input ref={audioInputRef} type="file" accept="audio/*"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size <= 10*1024*1024) setSelectedAudio(f); else toast.error('Max 10MB'); }}
                  className="hidden" />
                <button onClick={() => audioInputRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-wa-border rounded-lg hover:border-green-500 flex items-center justify-center gap-2">
                  <MusicNote size={24} /><span>{selectedAudio ? selectedAudio.name : 'Choose Audio File'}</span>
                </button>
                {selectedAudio && (
                  <div className="mt-3">
                    <audio src={URL.createObjectURL(selectedAudio)} controls className="w-full" />
                    <input value={imageCaption} onChange={(e) => setImageCaption(e.target.value)}
                      placeholder="Caption (optional)" className="w-full mt-2 px-3 py-2 border rounded-lg" maxLength={300} />
                  </div>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                <button onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-wa-border rounded-lg hover:border-green-500 flex items-center justify-center gap-2">
                  <ImageIcon size={24} /><span>{selectedImage ? selectedImage.name : 'Choose Image'}</span>
                </button>
                {selectedImage && (
                  <div className="mt-3">
                    <img src={URL.createObjectURL(selectedImage)} alt="Preview" className="rounded-lg max-h-48 object-cover" />
                    <textarea value={imageCaption} onChange={(e) => setImageCaption(e.target.value)}
                      placeholder="Caption (optional)" rows={2}
                      className="w-full mt-2 px-3 py-2 border rounded-lg resize-none" maxLength={300} />
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setShowAddModal(false); setStatusType('text'); setStatusContent(''); setSelectedImage(null); setSelectedAudio(null); setImageCaption(''); }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-wa-hover">Cancel</button>
              <button onClick={handleCreateStatus}
                disabled={uploading || (statusType === 'text' && !statusContent.trim()) || (statusType === 'image' && !selectedImage) || (statusType === 'audio' && !selectedAudio)}
                className="flex-1 px-4 py-2 bg-[#008069] text-white rounded-lg hover:bg-[#006e5a] disabled:opacity-50">
                {uploading ? 'Posting...' : 'Post Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusManagement;
